import requestUtils from 'api/requestUtils'
import { getCartItems } from 'api/cart'
import { getOfferById } from 'api/offer'
import { nonNullable } from 'lib/array/arrayUtils'
import { EmployeeTripRequestsResponse } from 'businessTraveller/types/tripRequestTypes'
import { isHotelLikeTripRequestItem, mapApprovalRequest, mapTripRequest, mapTripRequestItemsToHotelItems } from 'businessTraveller/utils/getTripRequestUtils'

export enum APPROVE_ORDER_REQUEST_GET_ERRORS {
  NOT_FOUND = 'Approve request not found!',
  OTHER = 'Something went wrong!',
}

interface Params {
  businessId: string;
  channelMarkup?: App.ChannelMarkup
  currentRegionCode: string;
  employeeId: string;
  employees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>;
}

async function requestGetApprovalRequestsAll(params: Params) {
  const { businessId, employees, currentRegionCode, channelMarkup } = params

  try {
    const tripResponse = await requestUtils.get<EmployeeTripRequestsResponse>(
      `/api/business-traveller/businesses/${businessId}/trip-requests/employee`,
      { credentials: 'include', excludeBrand: true },
    )

    const mappedRequests = await Promise.all(tripResponse.result.map(async(request) => {
      if (request.tripRequestItems.length > 0) {
        const tripRequestItem = request.tripRequestItems[0]

        if (isHotelLikeTripRequestItem(tripRequestItem)) {
          const hotelItems = mapTripRequestItemsToHotelItems(
            request.id,
            request.tripRequestItems.filter(isHotelLikeTripRequestItem),
            employees,
          )

          return mapTripRequest(request, tripRequestItem, employees, hotelItems)
        }

        return mapTripRequest(request, tripRequestItem, employees)
      } else {
        if (request.type === 'flight') {
          return mapApprovalRequest(request, employees)
        }

        try {
          const cartItems = await getCartItems(request.cartId)

          // Need to get the missing offer information that isn't included in the cart item.
          const hotelItems: Array<App.BusinessTraveller.HotelItem> = []
          for (const cartItem of cartItems.items) {
            if ('offerId' in cartItem &&
              (cartItem.itemType === 'bedbankHotel' || cartItem.itemType === 'hotel')
            ) {
              const offer: any = await getOfferById(cartItem.offerId, {
                region: currentRegionCode,
                channelMarkup,
              })

              const location = offer.locationHeading ?
            `${offer.locationHeading},  ${offer.locationSubheading}` :
            `${offer.property?.address?.city},  ${offer.property?.address?.stateProvinceName}`

              const hotelItem: App.BusinessTraveller.HotelItem = {
                id: cartItem.offerId,
                name: offer.name,
                location,
                image: offer.image.id,
                checkIn: 'checkIn' in cartItem ? cartItem.checkIn : '',
                checkOut: 'checkOut' in cartItem ? cartItem.checkOut : '',
                duration: cartItem.duration,
              }

              hotelItems.push(hotelItem)
            }
          }

          return mapApprovalRequest(request, employees, hotelItems)
        } catch
        {
          // skip missing carts
          return undefined
        }
      }
    }))

    return nonNullable(mappedRequests)
  } catch(error: any) {
    if (error?.status === 404) {
      return Promise.reject(APPROVE_ORDER_REQUEST_GET_ERRORS.NOT_FOUND)
    }
    return Promise.reject(APPROVE_ORDER_REQUEST_GET_ERRORS.OTHER)
  }
}

export default requestGetApprovalRequestsAll
